import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Globe, LayoutTemplate, List, ListChecks } from "lucide-react";
import { useAuth } from "../context/AuthContext.js";
import { useFontScale } from "../context/FontScaleContext.js";
import { ApiError, fetchMe, type MeProfile } from "../lib/api.js";
import { Avatar } from "./Avatar.js";
import { AiChatDock } from "./ai/AiChatDock.js";
import { BrandLogo } from "./BrandLogo.js";
import { FontScaleToggle } from "./FontScaleToggle.js";
import { ReportProblemDialog } from "./feedback/ReportProblemDialog.js";
import { ToastHost } from "./ui/Toast.js";
import { useGuardedNavigate, useUnsavedChanges } from "./UnsavedChangesProvider.js";

const NAV_ITEMS: { to: string; label: string; icon: ReactNode }[] = [
  { to: "/checklists", label: "Checklists", icon: <ListChecks size={16} aria-hidden="true" /> },
  { to: "/templates", label: "Templates", icon: <LayoutTemplate size={16} aria-hidden="true" /> },
  { to: "/lists", label: "Lists", icon: <List size={16} aria-hidden="true" /> },
  { to: "/community", label: "Community", icon: <Globe size={16} aria-hidden="true" /> },
];

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  [
    "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap px-2 py-1.5 text-sm font-medium no-underline transition sm:px-3",
    isActive
      ? "rounded-lg bg-brand-600 text-white shadow-sm hover:bg-brand-700"
      : "text-muted hover:text-ink",
  ].join(" ");

export function AppLayout() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const guardNav = useGuardedNavigate();
  const { confirmLeave } = useUnsavedChanges();
  const { scale: fontScale, cycleScale: cycleFontScale } = useFontScale();
  const [profile, setProfile] = useState<MeProfile | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // The menu item unmounts before the dialog opens, so Modal's saved
  // activeElement is already disconnected. Restore the account trigger instead.
  const reportFromMenuRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const me = await fetchMe();
        if (!cancelled) setProfile(me);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return;
      }
    }
    void load();
    // ProfilePage dispatches this after a save so the header avatar/name refresh.
    function onProfileUpdated() {
      void load();
    }
    window.addEventListener("profile-updated", onProfileUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener("profile-updated", onProfileUpdated);
    };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;

    function onPointerDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        triggerRef.current?.focus();
      }
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setMenuOpen(false);
        triggerRef.current?.focus();
      }
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const isAdmin = Boolean(user?.groups.includes("admin"));
  const screenName = profile?.screenName || user?.email || "Account";

  return (
    <div className="min-h-full" style={{ "--shell-header": "3.5rem" } as CSSProperties}>
      <header className="sticky top-0 z-40 h-14 border-b border-line bg-white">
        <div className="mx-auto flex h-full max-w-5xl items-center gap-3 px-4">
          <a
            href="#main"
            className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-50 focus:rounded-lg focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-ink focus:shadow-lg"
          >
            Skip to content
          </a>

          <Link to="/" className="shrink-0 no-underline" onClick={guardNav("/")}>
            <BrandLogo compactOnMobile />
          </Link>

          <nav className="flex min-w-0 flex-1 items-center gap-1" aria-label="Main">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                title={item.label}
                className={navLinkClass}
                onClick={guardNav(item.to)}
              >
                {item.icon}
                <span className="sr-only sm:not-sr-only">{item.label}</span>
              </NavLink>
            ))}
          </nav>

          <FontScaleToggle scale={fontScale} onCycle={cycleFontScale} />

          <div className="relative shrink-0" ref={menuRef}>
            <button
              ref={triggerRef}
              type="button"
              className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={`Account menu for ${screenName}`}
              onClick={() => setMenuOpen((open) => !open)}
            >
              {profile ? (
                <Avatar
                  sub={profile.sub}
                  screenName={profile.screenName}
                  firstName={profile.firstName}
                  lastName={profile.lastName}
                  color={profile.avatarColor}
                  avatarKey={profile.avatarKey}
                  googlePicture={profile.googlePicture}
                  updatedAt={profile.updatedAt}
                  size={32}
                />
              ) : (
                <Avatar
                  sub={user?.sub ?? "?"}
                  screenName={screenName}
                  size={32}
                />
              )}
            </button>

            {menuOpen ? (
              <div
                role="menu"
                className="absolute right-0 mt-2 w-48 rounded-2xl border border-line bg-white py-1 shadow-lg"
              >
                <Link
                  role="menuitem"
                  to="/profile"
                  className="block px-4 py-2 text-sm text-ink no-underline hover:bg-slate-50"
                  onClick={(e) => {
                    setMenuOpen(false);
                    guardNav("/profile")(e);
                  }}
                >
                  Profile
                </Link>
                <Link
                  role="menuitem"
                  to="/how-it-works"
                  className="block px-4 py-2 text-sm text-ink no-underline hover:bg-slate-50"
                  onClick={(e) => {
                    setMenuOpen(false);
                    guardNav("/how-it-works")(e);
                  }}
                >
                  How it works
                </Link>
                <button
                  role="menuitem"
                  type="button"
                  className="block w-full px-4 py-2 text-left text-sm text-ink hover:bg-slate-50"
                  onClick={() => {
                    setMenuOpen(false);
                    reportFromMenuRef.current = true;
                    setReportOpen(true);
                  }}
                >
                  Report a problem
                </button>
                {isAdmin ? (
                  <Link
                    role="menuitem"
                    to="/admin"
                    className="block px-4 py-2 text-sm text-ink no-underline hover:bg-slate-50"
                    onClick={(e) => {
                      setMenuOpen(false);
                      guardNav("/admin")(e);
                    }}
                  >
                    Admin
                  </Link>
                ) : null}
                <div className="my-1 border-t border-line" role="separator" />
                <button
                  role="menuitem"
                  type="button"
                  className="block w-full px-4 py-2 text-left text-sm text-ink hover:bg-slate-50"
                  onClick={() => {
                    setMenuOpen(false);
                    void confirmLeave().then((ok) => {
                      if (ok) signOut();
                    });
                  }}
                >
                  Sign out
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <main id="main">
        <Outlet />
      </main>

      <footer className="border-t border-line bg-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-6 gap-y-2 px-4 py-6 text-sm">
          <Link
            to="/contact"
            className="text-muted no-underline hover:text-ink"
            onClick={guardNav("/contact")}
          >
            Contact
          </Link>
          <Link
            to="/privacy"
            className="text-muted no-underline hover:text-ink"
            onClick={guardNav("/privacy")}
          >
            Privacy
          </Link>
          <button
            type="button"
            className="text-muted hover:text-ink"
            onClick={() => {
              reportFromMenuRef.current = false;
              setReportOpen(true);
            }}
          >
            Report a problem
          </button>
        </div>
      </footer>
      <ReportProblemDialog
        open={reportOpen}
        onClose={() => {
          setReportOpen(false);
          if (reportFromMenuRef.current) {
            reportFromMenuRef.current = false;
            triggerRef.current?.focus();
          }
        }}
      />
      <AiChatDock />
      <ToastHost />
    </div>
  );
}
